import {
  calcCount,
  calcPercent,
  mockInsightData,
} from '@/shared/job/insight.dto';
import { hasInsightItems } from '@/features/jobs/lib/hasInsightItems';
import { IOptions } from '@/shared/options';
import { cn } from '@/lib/utils/utils';
import { type LucideIcon } from 'lucide-react';
import { InsightCardIcon } from './InsightCardIcon';
import { insightBlurClass } from '@/features/jobs/lib/insightBlurClass';

const EDU_BAR_COLORS = ['bg-[#004DA0]', 'bg-[#0066CC]', 'bg-[#3385D6]'];
const EXP_BAR_COLORS = ['bg-secondary', 'bg-orange-400', 'bg-orange-300'];
export function InsightBarCard({
  title,
  icon,
  items,
  total,
  variant,
  isEnglish,
  headerTotal,
}: {
  title: string;
  icon: LucideIcon;
  items: (IOptions & { count: number })[];
  total: number;
  variant: 'edu' | 'exp';
  isEnglish: boolean;
  headerTotal?: number;
}) {
  const hasData = hasInsightItems(items);
  const sourceItems = hasData
    ? items
    : variant === 'edu'
      ? mockInsightData.edu
      : mockInsightData.exp;

  const colors = variant === 'edu' ? EDU_BAR_COLORS : EXP_BAR_COLORS;
  const track = variant === 'edu' ? 'bg-primary-100' : 'bg-orange-100';
  const rows = [...sourceItems].sort((a, b) => b.count - a.count).slice(0, 3);
  const percentBase =
    sourceItems.reduce((sum, item) => sum + item.count, 0) || total;

  const placeholderHeaderTotal =
    variant === 'edu'
      ? mockInsightData.edu.reduce((sum, item) => sum + item.count, 0)
      : mockInsightData.exp.reduce((sum, item) => sum + item.count, 0);

  return (
    <div className="flex h-full flex-col justify-between rounded-lg border border-gray-200 bg-white p-4 bg-primary">
      <div className="mb-4 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <InsightCardIcon icon={icon} />
          <h3 className="text-sm font-semibold leading-5 text-gray-900">
            {title}
          </h3>
        </div>
        {hasData && headerTotal !== undefined ? (
          <span className="shrink-0 text-sm font-semibold text-primary">
            {calcCount(headerTotal)}
          </span>
        ) : null}
      </div>
      <div className={cn(!hasData && insightBlurClass)} aria-hidden={!hasData}>
        {!hasData && headerTotal !== undefined ? (
          <p className="mb-4 text-right text-sm font-semibold text-primary">
            {calcCount(placeholderHeaderTotal)}
          </p>
        ) : null}
        <div className="space-y-3">
          {rows.map((item, index) => {
            const percentValue =
              percentBase > 0
                ? Math.round((item.count / percentBase) * 100)
                : 0;
            const percentLabel = calcPercent({
              total: percentBase,
              qty: item.count,
            }).replace(' ', '');

            return (
              <div key={item.id} className="space-y-1">
                <div className="flex items-center justify-between gap-2 text-xs">
                  <span className="truncate text-gray-700">
                    {isEnglish ? item.name_en : item.name_mn}
                  </span>
                  <span className="shrink-0 text-gray-500">{percentLabel}</span>
                </div>
                <div
                  className={cn(
                    'h-2 w-full overflow-hidden rounded-full',
                    track
                  )}
                >
                  <div
                    className={cn(
                      'h-full rounded-full',
                      colors[index] ?? colors[colors.length - 1]
                    )}
                    style={{ width: `${percentValue}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
